import React, { useState } from "react";
import * as Mui from "@mui/material";
import { ClientPropertyResource, UpsertClientPropertyResource } from "api/resource/client-property";
import { RichTextEditor } from "component/shared/rich-text-editor";
import { Select } from "component/shared/select";
import { FullPageCard } from "component/shared/full-page-card";
import { buildAgentFields } from "shared/agent-fields";
import { AgentControl } from "type/agent-control";
import { SupplementalStatus } from "type/supplemental-status";

export interface SupplementalFormFlags {
	// MAX-4194: one field, derived server-side. `showAgentPageColumn` is deliberately NOT here —
	// the "Show On Agent Page" *column* is read from the list page's own selector, never from this
	// prop, and its only in-form uses were the two gate lines this replaced. Keeping it would
	// leave a dead prop that tsc will not flag (noUnusedLocals is off).
	agentControl: AgentControl;
}

interface AgentOption {
	id: number;
	name: string;
}

interface Props {
	property: ClientPropertyResource | null;
	flags: SupplementalFormFlags;
	/**
	 * MAX-4194. The agent's "First Last" name resolved from a stored AID when the row has no
	 * typed name, or "" when there is nothing to seed. Resolved in edit.tsx, where the raw agent
	 * resources are in scope — see the note there on why agentOptions cannot be used.
	 */
	agentNameSeed: string;
	agents: AgentOption[];
	saving: boolean;
	/** Resolves true when the save landed — MAX-4194 re-baselines the agent dirty check on success. */
	onSave: (body: UpsertClientPropertyResource) => Promise<boolean>;
	onCancel: () => void;
}

interface FormState {
	showOnWebsite: boolean;
	status: string;
	listPrice: string;
	leasePrice: string;
	soldPrice: string;
	showSoldPriceYn: boolean;
	propertyType: string;
	bedrooms: number;
	fullBaths: number;
	bathHalf: number;
	yearBuilt: string;
	unitTotal: string;
	income: string;
	expenses: string;
	noi: string;
	taxes: string;
	classification: string[];
	classificationOther: string;
	zoneType: string;
	permitUses: string;
	make: string;
	model: string;
	propName: string;
	streetNumber: string;
	streetName: string;
	unitDesig: string;
	cityName: string;
	state: string;
	zip: string;
	addrFpn: string;
	water: string;
	sewer: string;
	loadingDocks: string;
	floors: string;
	ceilingHeight: string;
	overheadDoors: string;
	roadFrontage: string;
	parking: string;
	length: string;
	width: string;
	squareFeet: string;
	totlBldgSq: string;
	lotAcres: string;
	lotSqFt: string;
	mapYn: boolean;
	agentId: number;
	agentName: string;
	showOnAgentPage: boolean;
	propDescription: string;
	tourYn: boolean;
	tourUrl: string;
	tourLink: string;
	extraLinkYn: boolean;
	extraLinkUrl: string;
	extraLinkText: string;
	openYn: boolean;
	openDateText: string;
	openDate: string;
	openTime: string;
	openAgent: string;
	openHomeDirections: string;
	openHomeRemarks: string;
}

const NO_AGENT: AgentOption = { id: 0, name: "No agent" };

interface NumberOption {
	value: number;
	label: string;
}

interface StringOption {
	value: string;
	label: string;
}

const BEDROOM_OPTIONS: NumberOption[] = Array.from({ length: 13 }, (_, i) => ({ value: i, label: i === 0 ? "Any" : String(i) }));
const FULL_BATH_OPTIONS: NumberOption[] = Array.from({ length: 13 }, (_, i) => ({ value: i, label: String(i) }));
const HALF_BATH_OPTIONS: NumberOption[] = Array.from({ length: 11 }, (_, i) => ({ value: i, label: String(i) }));

const WATER_OPTIONS: StringOption[] = [
	{ value: "", label: "(none)" },
	{ value: "Yes", label: "Yes" },
	{ value: "No", label: "No" },
	{ value: "Well", label: "Well" },
];
const SEWER_OPTIONS: StringOption[] = [
	{ value: "", label: "(none)" },
	{ value: "Yes", label: "Yes" },
	{ value: "No", label: "No" },
	{ value: "Septic", label: "Septic" },
];

// Commercial classification checkbox group (cpropmodify_output_1.cfm) — stored as a comma list in
// the CLASSIFICATION column, with an "Other" free-text entry appended.
const CLASSIFICATION_OPTIONS = [
	"Office", "Development", "Apartments", "Conv. Store", "Retail", "Investment",
	"Restaurant", "Service Station", "Industrial", "Land", "Marina", "Hotel",
];

// The type picker offered in Add mode (cpropmodify_output_1.cfm:96-108). `code` is the initial stored
// property-type; the residential picker (SFR) lands on the House/Condo/Farm/Rental radio group.
const TYPE_PICKER: StringOption[] = [
	{ value: "SFR", label: "House & Condo" },
	{ value: "LL", label: "Lots & Land" },
	{ value: "RI", label: "Multi-Unit Residential" },
	{ value: "MH", label: "Mobile Home" },
	{ value: "COM", label: "Commercial" },
	{ value: "RNT", label: "Rental" },
];

// Radio choices shown when the property type is in the residential family (SFR/CND/FRM/RNT).
const PROPERTY_TYPE_RADIO: StringOption[] = [
	{ value: "SFR", label: "House" },
	{ value: "CND", label: "Condo" },
	{ value: "FRM", label: "Farm/Ranch" },
	{ value: "RNT", label: "Rental" },
];

// Property-type codes the edit form knows how to render. CF (cpropmodify_output_1.cfm:47-65) coerces
// any code outside this set to SFR when loading the form, defending against legacy/garbage codes
// (e.g. RIP) that would otherwise match no matrix branch. Add/picker mode always supplies a valid
// code, so this only guards the edit-load path.
const KNOWN_PROPERTY_TYPES = ["SFR", "LL", "RI", "MH", "COM", "CND", "FRM", "RNT"];

function normalizePropertyType(code: string | null | undefined): string {
	return code && KNOWN_PROPERTY_TYPES.includes(code) ? code : "SFR";
}

function toNumberOrNull(value: string): number | null {
	if (value === null || value.trim() === "") {
		return null;
	}
	const cleaned = value.replace(/,/g, "");
	const parsed = Number(cleaned);
	return isNaN(parsed) ? null : parsed;
}

function numToStr(value: number | null | undefined): string {
	return value == null ? "" : String(value);
}

function splitClassification(value: string | null | undefined): { known: string[]; other: string } {
	if (!value) {
		return { known: [], other: "" };
	}
	const parts = value.split(",").map((p) => p.trim()).filter(Boolean);
	const known: string[] = [];
	const other: string[] = [];
	parts.forEach((p) => {
		if (CLASSIFICATION_OPTIONS.includes(p)) {
			known.push(p);
		} else {
			other.push(p);
		}
	});
	return { known, other: other.join(", ") };
}

function initFromProperty(property: ClientPropertyResource): FormState {
	const classification = splitClassification(property.classification);
	return {
		showOnWebsite: !!property.showOnWebsite,
		status: property.status || SupplementalStatus.ACTIVE.id,
		listPrice: numToStr(property.listPrice),
		leasePrice: numToStr(property.leasePrice),
		soldPrice: numToStr(property.soldPrice),
		showSoldPriceYn: !!property.showSoldPriceYn,
		propertyType: normalizePropertyType(property.propertyType),
		bedrooms: property.bedrooms == null ? 1 : property.bedrooms,
		fullBaths: property.fullBaths == null ? 1 : property.fullBaths,
		bathHalf: property.bathHalf == null ? 0 : property.bathHalf,
		yearBuilt: property.yearBuilt || "",
		unitTotal: numToStr(property.unitTotal),
		income: numToStr(property.income),
		expenses: property.expenses || "",
		noi: property.noi || "",
		taxes: property.taxes || "",
		classification: classification.known,
		classificationOther: classification.other,
		zoneType: property.zoneType || "",
		permitUses: property.permitUses || "",
		make: property.make || "",
		model: property.model || "",
		propName: property.propName || "",
		streetNumber: property.streetNumber || "",
		streetName: property.streetName || "",
		unitDesig: property.unitDesig || "",
		cityName: property.cityName || "",
		state: property.state || "",
		zip: property.zip || "",
		addrFpn: property.addrFpn || "F",
		water: property.water || "",
		sewer: property.sewer || "",
		loadingDocks: property.loadingDocks || "",
		floors: property.floors || "",
		ceilingHeight: property.ceilingHeight || "",
		overheadDoors: property.overheadDoors || "",
		roadFrontage: property.roadFrontage || "",
		parking: property.parking || "",
		length: property.length || "",
		width: property.width || "",
		squareFeet: numToStr(property.squareFeet),
		totlBldgSq: numToStr(property.totlBldgSq),
		lotAcres: numToStr(property.lotAcres),
		lotSqFt: numToStr(property.lotSqFt),
		mapYn: property.mapYn == null ? true : !!property.mapYn,
		agentId: property.agentId || 0,
		agentName: property.agentName || "",
		showOnAgentPage: !!property.showOnAgentPage,
		propDescription: property.propDescription || "",
		tourYn: !!property.tourYn,
		tourUrl: property.tourUrl || "",
		tourLink: property.tourLink || "Virtual Tour",
		extraLinkYn: !!property.extraLinkYn,
		extraLinkUrl: property.extraLinkUrl || "",
		extraLinkText: property.extraLinkText || "Click Here!",
		openYn: !!property.openYn,
		openDateText: property.openDateText || "",
		openDate: property.openDate ? String(property.openDate).substring(0, 10) : "",
		openTime: property.openTime || "",
		openAgent: property.openAgent || "",
		openHomeDirections: property.openHomeDirections || "",
		openHomeRemarks: property.openHomeRemarks || "",
	};
}

// Add-mode blank form for a chosen type (cpropmodify_output_1.cfm:33-44 defaults).
function initDefaults(propertyType: string): FormState {
	return {
		showOnWebsite: true,
		status: SupplementalStatus.ACTIVE.id,
		listPrice: "",
		leasePrice: "",
		soldPrice: "",
		showSoldPriceYn: false,
		propertyType,
		bedrooms: 1,
		fullBaths: 1,
		bathHalf: 0,
		yearBuilt: "",
		unitTotal: "",
		income: "",
		expenses: "",
		noi: "",
		taxes: "",
		classification: [],
		classificationOther: "",
		zoneType: "",
		permitUses: "",
		make: "",
		model: "",
		propName: "",
		streetNumber: "",
		streetName: "",
		unitDesig: "",
		cityName: "",
		state: "",
		zip: "",
		addrFpn: "F",
		water: "",
		sewer: "",
		loadingDocks: "",
		floors: "",
		ceilingHeight: "",
		overheadDoors: "",
		roadFrontage: "",
		parking: "",
		length: "",
		width: "",
		squareFeet: "0",
		totlBldgSq: "",
		lotAcres: "",
		lotSqFt: "",
		mapYn: true,
		agentId: 0,
		agentName: "",
		showOnAgentPage: true,
		propDescription: "",
		tourYn: false,
		tourUrl: "",
		tourLink: "Virtual Tour",
		extraLinkYn: false,
		extraLinkUrl: "",
		extraLinkText: "Click Here!",
		openYn: false,
		openDateText: "",
		openDate: "",
		openTime: "",
		openAgent: "",
		openHomeDirections: "",
		openHomeRemarks: "",
	};
}

export const SupplementalForm: React.FC<Props> = ({ property, flags, agentNameSeed, agents, saving, onSave, onCancel }) => {
	const [form, setForm] = useState<FormState | null>(() => (property ? initFromProperty(property) : null));

	// MAX-4194: the dirty baseline for the free-text agent box.
	//
	// It must be the *seeded* value, not the server's agentName. On a seeded row the server
	// returned "" by definition of the seed precondition, so a baseline read from the resource
	// could never equal the box: every untouched save would look like a rename, write the seed
	// into the name columns and zero AID, and a genuine clear (typed === "" && baseline === "")
	// would send nothing at all.
	const [agentNameBaseline, setAgentNameBaseline] = useState<string>(
		() => property?.agentName || agentNameSeed || ""
	);

	// The seed and the baseline must be captured together, exactly once.
	//
	// State above initialises lazily and there is no resync, while the form mounts as soon as
	// `property` arrives — before fetchClientProperties and fetchAgents have necessarily
	// resolved. On a hard refresh or a deep link the seed is therefore "" at mount and would
	// never re-run, so the feature would be silently absent in its main scenario.
	//
	// The guard is "the user has not touched the box", NOT "the box is empty". Those differ once
	// the user is in the box, and the weaker version is actively wrong: on a slow load the seed
	// can arrive after the user has typed, the effect skips without arming, and then the moment
	// they clear the box it refills itself with a name they never entered — silently converting
	// their deliberate clear into a no-op save.
	//
	// Only gated on freeText because that is the only mode rendering this box; seeding in the
	// other two put a fabricated value into form state that nothing displayed.
	const agentBoxTouched = React.useRef(false);
	const seedApplied = React.useRef(false);
	React.useEffect(() => {
		if (seedApplied.current || agentBoxTouched.current || flags.agentControl !== "freeText") {
			return;
		}
		if (!agentNameSeed) {
			return; // seed not resolved yet — stay armed, this is the deep-link race
		}
		seedApplied.current = true;
		if (!form || form.agentName !== "") {
			return; // seed resolved but the box is already populated: nothing to do, now disarmed
		}
		setAgentNameBaseline(agentNameSeed);
		setForm((prev) => (prev ? { ...prev, agentName: agentNameSeed } : prev));
	}, [agentNameSeed, form, flags.agentControl]);

	// Status / Property Type radios lay out in a row on tablet+ and stack in a column on mobile.
	const theme = Mui.useTheme();
	const radiosRow = Mui.useMediaQuery(theme.breakpoints.up("sm"));

	const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
	};

	// Add mode, no type chosen yet → show the type picker (cpropmodify_output_1.cfm:96-110).
	if (!form) {
		return (
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid item>
					<Mui.Typography variant="h6">What type of listing would you like to add?</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item container spacing={2}>
					{TYPE_PICKER.map((option) => (
						<Mui.Grid item key={option.value}>
							<Mui.Button
								variant="outlined"
								color="primary"
								onClick={() => setForm(initDefaults(option.value))}
								data-testid={`supplemental-type-${option.value}`}
							>
								{option.label}
							</Mui.Button>
						</Mui.Grid>
					))}
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Button variant="outlined" onClick={onCancel}>
						Cancel
					</Mui.Button>
				</Mui.Grid>
			</Mui.Grid>
		);
	}

	const pt = form.propertyType;
	const isCom = pt === "COM";
	const isRi = pt === "RI" || pt === "RIP";
	const isMh = pt === "MH";
	const isLl = pt === "LL";
	const isResidential = ["SFR", "CND", "RNT", "FRM", "MH"].includes(pt);
	const showPropertyTypeRadio = ["SFR", "CND", "FRM", "RNT"].includes(pt);
	const showYearBuilt = !isLl;
	const showSquareFeet = !isLl;
	const isSold = SupplementalStatus.getById(form.status)?.sold ?? false;

	const showAgentDropdown = flags.agentControl === "dropdown";
	const showAgentFreeText = flags.agentControl === "freeText";
	const agentOptions = [NO_AGENT, ...agents];

	const toggleClassification = (option: string) => {
		setForm((prev) => {
			if (!prev) {
				return prev;
			}
			const has = prev.classification.includes(option);
			return {
				...prev,
				classification: has
					? prev.classification.filter((c) => c !== option)
					: [...prev.classification, option],
			};
		});
	};

	const buildClassification = (): string | null => {
		const parts = [...form.classification];
		if (form.classificationOther.trim()) {
			parts.push(form.classificationOther.trim());
		}
		return parts.length ? parts.join(", ") : null;
	};

	const handleSave = async () => {
		// MAX-4194: built once so the same object decides both what is sent and what the new
		// baseline becomes.
		const agentFields = buildAgentFields(form, flags.agentControl, property ? agentNameBaseline : null);
		const body: UpsertClientPropertyResource = {
			boardId: property ? property.boardId : 0,
			listingNumber: property ? property.listingNumber : null,
			showOnWebsite: form.showOnWebsite,
			status: form.status,
			listPrice: toNumberOrNull(form.listPrice),
			leasePrice: isCom ? toNumberOrNull(form.leasePrice) : null,
			soldPrice: toNumberOrNull(form.soldPrice),
			showSoldPriceYn: form.showSoldPriceYn,
			propertyType: form.propertyType,
			bedrooms: isResidential ? form.bedrooms : null,
			fullBaths: isResidential ? form.fullBaths : null,
			bathHalf: isResidential ? form.bathHalf : null,
			yearBuilt: showYearBuilt ? form.yearBuilt || null : null,
			unitTotal: isRi ? toNumberOrNull(form.unitTotal) : null,
			income: isCom ? toNumberOrNull(form.income) : null,
			expenses: isCom ? form.expenses || null : null,
			noi: isCom ? form.noi || null : null,
			taxes: isCom ? form.taxes || null : null,
			classification: isCom ? buildClassification() : null,
			zoneType: isCom ? form.zoneType || null : null,
			permitUses: isCom ? form.permitUses || null : null,
			make: isMh ? form.make || null : null,
			model: isMh ? form.model || null : null,
			propName: isCom ? form.propName || null : null,
			streetNumber: form.streetNumber || null,
			streetName: form.streetName || null,
			unitDesig: isResidential ? form.unitDesig || null : null,
			cityName: form.cityName || null,
			state: form.state || null,
			zip: form.zip || null,
			addrFpn: form.addrFpn || null,
			water: isCom ? form.water || null : null,
			sewer: isCom ? form.sewer || null : null,
			loadingDocks: isCom ? form.loadingDocks || null : null,
			floors: isCom ? form.floors || null : null,
			ceilingHeight: isCom ? form.ceilingHeight || null : null,
			overheadDoors: isCom ? form.overheadDoors || null : null,
			roadFrontage: isCom ? form.roadFrontage || null : null,
			parking: isCom ? form.parking || null : null,
			length: isMh ? form.length || null : null,
			width: isMh ? form.width || null : null,
			squareFeet: showSquareFeet ? toNumberOrNull(form.squareFeet) : null,
			totlBldgSq: isCom ? toNumberOrNull(form.totlBldgSq) : null,
			lotAcres: toNumberOrNull(form.lotAcres),
			lotSqFt: toNumberOrNull(form.lotSqFt),
			mapYn: form.mapYn,
			// MAX-4194: agent keys come from shared/agent-fields — do not inline. Deleting this
			// spread compiles silently: Step 10 made all four keys optional on the Upsert type,
			// and TypeScript does not excess-property-check spread-contributed keys either.
			...agentFields,
			propDescription: form.propDescription || null,
			openYn: form.openYn,
			openDateText: form.openDateText || null,
			openDate: form.openDate || null,
			openTime: form.openTime || null,
			openAgent: form.openAgent || null,
			openHomeDirections: form.openHomeDirections || null,
			openHomeRemarks: form.openHomeRemarks || null,
			// F1: when SOLD, CF omits the tour/extra-link fields from the write entirely
			// (cpropmodify_output_1.cfm:750/824 — the sections are not rendered) so prior tour data is
			// preserved. Sending false/null would wipe it via the server's containsField gating, so we
			// leave the six fields off the body when SOLD rather than nulling them (MAX-3876 F1).
			...(isSold
				? {}
				: {
						tourYn: form.tourYn,
						tourUrl: form.tourUrl || null,
						tourLink: form.tourLink || null,
						extraLinkYn: form.extraLinkYn,
						extraLinkUrl: form.extraLinkUrl || null,
						extraLinkText: form.extraLinkText || null,
				  }),
		};
		const saved = await onSave(body);
		// MAX-4194: re-baseline to what was actually written, or the next edit in this same page
		// visit is compared against a stale value. The page stays mounted after an update, so
		// rename -> save -> rename back -> save would otherwise emit nothing at all and report
		// success over an unchanged row; worse, every later incidental save would keep re-sending
		// the name and re-zeroing AID, which is the bleed this whole change exists to stop.
		//
		// Only on success, and only in edit mode — a create navigates away.
		if (saved && property && agentFields.agentName !== undefined) {
			setAgentNameBaseline(agentFields.agentName);
		}
	};

	const switchField = (label: string, key: keyof FormState) => (
		<Mui.FormControlLabel
			control={
				<Mui.Switch
					color="primary"
					checked={!!(form as any)[key]}
					onChange={(e) => set(key, e.target.checked as any)}
				/>
			}
			label={label}
		/>
	);

	const text = (
		label: string,
		key: keyof FormState,
		opts?: { maxLength?: number; adornment?: string }
	) => (
		<Mui.Grid item>
			<Mui.TextField
				fullWidth
				label={label}
				variant="outlined"
				size="small"
				InputProps={
					opts && opts.adornment
						? { startAdornment: <Mui.InputAdornment position="start">{opts.adornment}</Mui.InputAdornment> }
						: undefined
				}
				inputProps={{ maxLength: opts && opts.maxLength }}
				value={String((form as any)[key] ?? "")}
				onChange={(e) => set(key, e.target.value as any)}
			/>
		</Mui.Grid>
	);

	// Each section renders as its own card (emulating /settings/listing-details).
	const sectionCard = (content: React.ReactNode) => (
		<Mui.Box mb={2}>
			<FullPageCard>
				<Mui.Grid container direction="column" spacing={2} style={{ maxWidth: 560 }}>
					{content}
				</Mui.Grid>
			</FullPageCard>
		</Mui.Box>
	);

	return (
		<>
			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Property Information</Mui.Typography>
					</Mui.Grid>

			<Mui.Grid item>{switchField("Show On Website", "showOnWebsite")}</Mui.Grid>

			<Mui.Grid item>
				<Mui.FormControl component="fieldset">
					<Mui.FormLabel component="legend" style={{ fontWeight: 600 }}>Property Status</Mui.FormLabel>
					<Mui.RadioGroup
						row={radiosRow}
						value={form.status}
						onChange={(e) => set("status", e.target.value)}
						data-testid="supplemental-status"
					>
						{SupplementalStatus.values().map((status) => (
							<Mui.FormControlLabel
								key={status.id}
								value={status.id}
								control={<Mui.Radio color="primary" size="small" />}
								label={status.label}
							/>
						))}
					</Mui.RadioGroup>
				</Mui.FormControl>
			</Mui.Grid>

			{showPropertyTypeRadio && (
				<Mui.Grid item>
					<Mui.FormControl component="fieldset">
						<Mui.FormLabel component="legend" style={{ fontWeight: 600 }}>Property Type</Mui.FormLabel>
						<Mui.RadioGroup row={radiosRow} value={form.propertyType} onChange={(e) => set("propertyType", e.target.value)}>
							{PROPERTY_TYPE_RADIO.map((option) => (
								<Mui.FormControlLabel
									key={option.value}
									value={option.value}
									control={<Mui.Radio color="primary" size="small" />}
									label={option.label}
								/>
							))}
						</Mui.RadioGroup>
					</Mui.FormControl>
				</Mui.Grid>
			)}

			{text("List Price", "listPrice", { maxLength: 10, adornment: "$" })}
			{isCom && text("Rent/Lease Price", "leasePrice", { maxLength: 10, adornment: "$" })}
			{text("Sold Price", "soldPrice", { maxLength: 10, adornment: "$" })}
			<Mui.Grid item>{switchField("Show Sold Price on Website", "showSoldPriceYn")}</Mui.Grid>

			{isResidential && (
				<>
					<Mui.Grid item>
						<Select
							fullWidth
							variant="outlined"
							label="Bedrooms"
							value={form.bedrooms}
							options={BEDROOM_OPTIONS}
							valueExtractor={(option: NumberOption) => option.value}
							labelExtractor={(option: NumberOption) => option.label}
							optionRenderer={(option: NumberOption) => <Mui.ListItemText primary={option.label} />}
							onChange={(value) => set("bedrooms", value == null || value === "" ? 0 : Number(value))}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Grid container spacing={2}>
							<Mui.Grid item xs={6}>
								<Select
									fullWidth
									variant="outlined"
									label="Full Bathrooms"
									value={form.fullBaths}
									options={FULL_BATH_OPTIONS}
									valueExtractor={(option: NumberOption) => option.value}
									labelExtractor={(option: NumberOption) => option.label}
									optionRenderer={(option: NumberOption) => <Mui.ListItemText primary={option.label} />}
									onChange={(value) => set("fullBaths", value == null || value === "" ? 0 : Number(value))}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={6}>
								<Select
									fullWidth
									variant="outlined"
									label="Half Bathrooms"
									value={form.bathHalf}
									options={HALF_BATH_OPTIONS}
									valueExtractor={(option: NumberOption) => option.value}
									labelExtractor={(option: NumberOption) => option.label}
									optionRenderer={(option: NumberOption) => <Mui.ListItemText primary={option.label} />}
									onChange={(value) => set("bathHalf", value == null || value === "" ? 0 : Number(value))}
								/>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>
				</>
			)}

			{showYearBuilt && text("Year Built", "yearBuilt", { maxLength: 10 })}
			{isRi && text("Total Units", "unitTotal", { maxLength: 10 })}
			{showSquareFeet && text("Square Feet", "squareFeet", { maxLength: 10 })}
			{isCom && text("Total Bldg Sq Ft", "totlBldgSq", { maxLength: 10 })}
			{text("Lot Acres", "lotAcres", { maxLength: 10 })}
			{text("Lot Sq Ft", "lotSqFt", { maxLength: 10 })}

			{isMh && (
				<>
					{text("Make", "make", { maxLength: 50 })}
					{text("Model", "model", { maxLength: 50 })}
					{text("Length", "length", { maxLength: 20 })}
					{text("Width", "width", { maxLength: 20 })}
				</>
			)}

			{isCom && (
				<>
					{text("Property Name", "propName", { maxLength: 100 })}
					{text("Income", "income", { maxLength: 20 })}
					{text("Expenses", "expenses", { maxLength: 20 })}
					{text("Net Oper. Income", "noi", { maxLength: 20 })}
					{text("Taxes", "taxes", { maxLength: 20 })}
					{text("Zone", "zoneType", { maxLength: 50 })}
					<Mui.Grid item>
						<Mui.TextField
							fullWidth
							multiline
							minRows={2}
							label="Permitted Uses"
							variant="outlined"
							size="small"
							value={form.permitUses}
							onChange={(e) => set("permitUses", e.target.value)}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Select
							fullWidth
							variant="outlined"
							label="Water"
							value={form.water}
							options={WATER_OPTIONS}
							valueExtractor={(option: StringOption) => option.value}
							labelExtractor={(option: StringOption) => option.label}
							optionRenderer={(option: StringOption) => <Mui.ListItemText primary={option.label} />}
							onChange={(value) => set("water", value == null ? "" : String(value))}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Select
							fullWidth
							variant="outlined"
							label="Sewer"
							value={form.sewer}
							options={SEWER_OPTIONS}
							valueExtractor={(option: StringOption) => option.value}
							labelExtractor={(option: StringOption) => option.label}
							optionRenderer={(option: StringOption) => <Mui.ListItemText primary={option.label} />}
							onChange={(value) => set("sewer", value == null ? "" : String(value))}
						/>
					</Mui.Grid>
					{text("Loading Docks", "loadingDocks", { maxLength: 20 })}
					{text("Floors", "floors", { maxLength: 20 })}
					{text("Ceiling Height", "ceilingHeight", { maxLength: 20 })}
					{text("Overhead Doors", "overheadDoors", { maxLength: 20 })}
					{text("Road Frontage", "roadFrontage", { maxLength: 20 })}
					{text("Parking", "parking", { maxLength: 20 })}
					<Mui.Grid item>
						<Mui.FormControl component="fieldset">
							<Mui.FormLabel component="legend" style={{ fontWeight: 600 }}>Classification</Mui.FormLabel>
							<Mui.FormGroup row>
								{CLASSIFICATION_OPTIONS.map((option) => (
									<Mui.FormControlLabel
										key={option}
										control={
											<Mui.Checkbox
												color="primary"
												size="small"
												checked={form.classification.includes(option)}
												onChange={() => toggleClassification(option)}
											/>
										}
										label={option}
									/>
								))}
							</Mui.FormGroup>
						</Mui.FormControl>
					</Mui.Grid>
					{text("Other Classification", "classificationOther", { maxLength: 100 })}
				</>
			)}

			<Mui.Grid item>
				<Mui.TextField
					fullWidth label="Address Number" variant="outlined" size="small"
					inputProps={{ maxLength: 50, "data-testid": "supplemental-street-number" }}
					value={form.streetNumber} onChange={(e) => set("streetNumber", e.target.value)}
				/>
			</Mui.Grid>
			<Mui.Grid item>
				<Mui.TextField
					fullWidth label="Street Name" variant="outlined" size="small"
					inputProps={{ maxLength: 50, "data-testid": "supplemental-street-name" }}
					value={form.streetName} onChange={(e) => set("streetName", e.target.value)}
				/>
			</Mui.Grid>
			{isResidential && (
				<Mui.Grid item>
					<Mui.TextField
						fullWidth label="Unit Number" variant="outlined" size="small"
						inputProps={{ maxLength: 50 }}
						value={form.unitDesig} onChange={(e) => set("unitDesig", e.target.value)}
					/>
				</Mui.Grid>
			)}

			<Mui.Grid item>
				<Mui.TextField
					fullWidth label="City" variant="outlined" size="small"
					inputProps={{ maxLength: 50, "data-testid": "supplemental-city" }}
					value={form.cityName} onChange={(e) => set("cityName", e.target.value)}
				/>
			</Mui.Grid>
			<Mui.Grid item>
				<Mui.Grid container spacing={2}>
					<Mui.Grid item xs={6}>
						<Mui.TextField
							fullWidth label="State" variant="outlined" size="small"
							inputProps={{ maxLength: 2 }}
							value={form.state} onChange={(e) => set("state", e.target.value)}
						/>
					</Mui.Grid>
					<Mui.Grid item xs={6}>
						<Mui.TextField
							fullWidth label="Zip" variant="outlined" size="small"
							inputProps={{ maxLength: 20 }}
							value={form.zip} onChange={(e) => set("zip", e.target.value)}
						/>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Grid>

			<Mui.Grid item>
				<Mui.FormControl component="fieldset">
					<Mui.FormLabel component="legend" style={{ fontWeight: 600 }}>Show Address</Mui.FormLabel>
					<Mui.RadioGroup row={radiosRow} value={form.addrFpn} onChange={(e) => set("addrFpn", e.target.value)}>
						<Mui.FormControlLabel value="F" control={<Mui.Radio color="primary" size="small" />} label="Address # & Street Name" />
						<Mui.FormControlLabel value="P" control={<Mui.Radio color="primary" size="small" />} label="Street Only" />
						<Mui.FormControlLabel value="N" control={<Mui.Radio color="primary" size="small" />} label="No Address" />
					</Mui.RadioGroup>
				</Mui.FormControl>
			</Mui.Grid>

			{showAgentDropdown && (
				<>
					<Mui.Grid item>
						<Select
							fullWidth
							variant="outlined"
							label="Agent"
							value={form.agentId}
							options={agentOptions}
							valueExtractor={(option: AgentOption) => option.id}
							labelExtractor={(option: AgentOption) => option.name}
							optionRenderer={(option: AgentOption) => <Mui.ListItemText primary={option.name} />}
							onChange={(value) => set("agentId", value == null || value === "" ? 0 : Number(value))}
						/>
					</Mui.Grid>
					<Mui.Grid item>{switchField("Show On Agent Page", "showOnAgentPage")}</Mui.Grid>
				</>
			)}
			{showAgentFreeText && (
				<Mui.Grid item>
					<Mui.TextField
						fullWidth label="Agent Name" variant="outlined" size="small"
						value={form.agentName}
						onChange={(e) => {
							// MAX-4194: disarm the seed permanently — see the effect above.
							agentBoxTouched.current = true;
							set("agentName", e.target.value);
						}}
					/>
				</Mui.Grid>
			)}
				</>
			)}

			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Map Settings</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>{switchField("Map ON?", "mapYn")}</Mui.Grid>
					<Mui.Grid item>
						<Mui.Typography variant="body2" color="textSecondary" component="div">
							If set to Yes:
							<ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
								<li>
									the property detail page for this listing will include a "Map Location" link
									that shows the property on a map. The link will only appear if the full address
									can be displayed.
								</li>
								<li>
									listings pages that include a map (e.g., search results) will include this
									listing on the map.
								</li>
							</ul>
							If you do not want to include the Map Location link or do not want the listing to
							appear on the results map, set this option to No. It may be useful to set this option
							to No if the listing is being displayed on the map incorrectly.
						</Mui.Typography>
					</Mui.Grid>
				</>
			)}

			{sectionCard(
				<Mui.Grid item>
					<Mui.Typography variant="h6">Description</Mui.Typography>
					<RichTextEditor
						toolbarId="supplemental-desc-toolbar"
						value={form.propDescription}
						onChange={(value: string) => set("propDescription", value)}
					/>
				</Mui.Grid>
			)}

			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Other Settings</Mui.Typography>
					</Mui.Grid>
					{!isSold && (
						<>
							<Mui.Grid item data-testid="supplemental-tour-toggle">
								{switchField("Show Virtual Tour", "tourYn")}
							</Mui.Grid>
							{form.tourYn && (
								<>
									<Mui.Grid item>
										<Mui.TextField fullWidth label="Tour URL" variant="outlined" size="small"
											inputProps={{ maxLength: 1024, "data-testid": "supplemental-tour-url" }}
											value={form.tourUrl} onChange={(e) => set("tourUrl", e.target.value)} />
									</Mui.Grid>
									<Mui.Grid item>
										<Mui.TextField fullWidth label="Tour Link Text" variant="outlined" size="small"
											inputProps={{ maxLength: 64 }}
											value={form.tourLink} onChange={(e) => set("tourLink", e.target.value)} />
									</Mui.Grid>
								</>
							)}
							<Mui.Grid item data-testid="supplemental-extralink-toggle">
								{switchField("Show Extra Link", "extraLinkYn")}
							</Mui.Grid>
							{form.extraLinkYn && (
								<>
									<Mui.Grid item>
										<Mui.TextField fullWidth label="Extra Link URL" variant="outlined" size="small"
											inputProps={{ maxLength: 128, "data-testid": "supplemental-extra-url" }}
											value={form.extraLinkUrl} onChange={(e) => set("extraLinkUrl", e.target.value)} />
									</Mui.Grid>
									<Mui.Grid item>
										<Mui.TextField fullWidth label="Extra Link Text" variant="outlined" size="small"
											inputProps={{ maxLength: 64 }}
											value={form.extraLinkText} onChange={(e) => set("extraLinkText", e.target.value)} />
									</Mui.Grid>
								</>
							)}
						</>
					)}

					<Mui.Grid item>{switchField("Show Open House", "openYn")}</Mui.Grid>
					{form.openYn && (
						<>
							{text("Open Date Text", "openDateText", { maxLength: 99 })}
							<Mui.Grid item>
								<Mui.TextField fullWidth label="Open Date" type="date" variant="outlined" size="small"
									InputLabelProps={{ shrink: true }}
									value={form.openDate} onChange={(e) => set("openDate", e.target.value)} />
							</Mui.Grid>
							{text("Open Time", "openTime", { maxLength: 50 })}
							{text("Open Agent", "openAgent", { maxLength: 50 })}
							<Mui.Grid item>
								<Mui.TextField fullWidth multiline minRows={2} label="Directions" variant="outlined" size="small"
									value={form.openHomeDirections} onChange={(e) => set("openHomeDirections", e.target.value)} />
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.TextField fullWidth multiline minRows={2} label="Remarks" variant="outlined" size="small"
									value={form.openHomeRemarks} onChange={(e) => set("openHomeRemarks", e.target.value)} />
							</Mui.Grid>
						</>
					)}
				</>
			)}

			<Mui.Box mb={2}>
				<Mui.Grid container spacing={2}>
					<Mui.Grid item>
						<Mui.Button
							variant="contained" color="primary"
							onClick={handleSave}
							disabled={saving}
							data-testid="supplemental-save"
						>
							Save Property Information
						</Mui.Button>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button variant="outlined" onClick={onCancel} disabled={saving}>
							Cancel
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Box>
		</>
	);
};
